import HttpClient from '../httpClient';
import { USER_ROLES } from '../../constants';

export type TeamRole =
  | typeof USER_ROLES.ROOT_USER
  | typeof USER_ROLES.ADMIN
  | typeof USER_ROLES.RECRUITER
  | typeof USER_ROLES.SALES;

export type TeamMemberStatus = 'ACTIVE' | 'PENDING' | 'INACTIVE';

/** Mirrors userInfo / getProfile — granular notification toggles */
export interface MemberEmailPreferences {
  jobPublished?: boolean;
  jobPaused?: boolean;
  jobClosed?: boolean;
  featureUpdates?: boolean;
}

export interface MemberInAppPreferences {
  jobPublished?: boolean;
  jobPaused?: boolean;
  jobClosed?: boolean;
  featureUpdates?: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  country?: string;
  joinedAt?: string;
  createdAt?: string;
  role: TeamRole;
  workspaces: Array<{ id: string; name: string }>;
  status: TeamMemberStatus;
  avatarUrl?: string;
  /** From `getMember` / profile — not included on `listMembers` */
  emailNotifications?: boolean;
  inAppNotifications?: boolean;
  emailPreferences?: MemberEmailPreferences;
  inAppPreferences?: MemberInAppPreferences;
  userId?: string;
}

class TeamController extends HttpClient {
  listMembers = async (token: string, orgUuid?: string) => {
    // Support both /team/members/{orgUuid} and /team/members?orgUuid={orgUuid}
    const url = `/team/${orgUuid}/members`;
    const response = await this.client.get(url, {
      headers: {
        Authorization: token,
      },
    });
    // Response structure: { success: true, data: TeamMember[] }
    return (response.data?.data || response.data) as TeamMember[];
  };

  getMember = async (orgUuid: string, token: string) => {
    const url = `/team/${orgUuid}/member`;
    const response = await this.client.get(url, {
      headers: {
        Authorization: token,
      },
    });
    // Response structure: { success: true, data: TeamMember }
    return (response.data?.data || response.data) as TeamMember;
  };

  addMember = async (
    payload: {
      firstName: string;
      lastName: string;
      email: string;
      role: TeamRole;
      orgUuid: string;
    },
    token: string,
  ) => {
    const response = await this.client.post('/team/members', payload, {
      headers: {
        Authorization: token,
      },
    });
    return response.data as TeamMember;
  };

  updateMemberRole = async (
    memberId: string,
    role: TeamRole,
    token: string,
    orgUuid?: string,
  ) => {
    const response = await this.client.patch(
      `/team/members/${memberId}/role`,
      { role, orgUuid },
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data as TeamMember;
  };

  removeMember = async (memberId: string, orgUuid: string, token: string) => {
    const response = await this.client.delete(`/team/members/${memberId}`, {
      data: JSON.stringify({ orgUuid }),
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  };

  leaveCompany = async (
    orgUuid: string,
    token: string,
    transferToEmail?: string,
  ) => {
    const response = await this.client.delete('/team/members/leave', {
      data: JSON.stringify({ orgUuid, transferToEmail }),
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  };

  resendInvite = async (memberId: string, orgUuid: string, token: string) => {
    const response = await this.client.post(
      `/team/members/${memberId}/resend-invite`,
      { orgUuid },
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  acceptInvitation = async (
    payload: {
      orgUuid: string;
    },
    token: string,
  ) => {
    const response = await this.client.post(
      '/team/invitation/accept',
      payload,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  addCompany = async (
    payload: {
      organisationName: string;
      organisationLogo?: string;
      organisationLogoUrl?: string;
      description?: unknown;
      workspaceId?: string;
      firstName?: string;
      lastName?: string;
      setupStatus?: string;
      industry?: string;
      website?: string;
      companySize?: string;
    },
    token: string,
  ) => {
    const response = await this.client.post('/team/company', payload, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  getCompany = async (orgId: string, token: string) => {
    const response = await this.client.get(`/team/company/${orgId}`, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  };

  updateCompany = async (
    orgId: string,
    payload: {
      organisationName?: string;
      organisationLogo?: string;
      organisationLogoUrl?: string;
      description?: unknown;
      industry?: string;
      website?: string;
      companySize?: string;
      setupStatus?: string;
    },
    token: string,
  ) => {
    const response = await this.client.patch(
      `/team/company/${orgId}`,
      payload,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  };

  updateMemberProfile = async (
    payload: {
      orgId: string;
      name?: string;
      phoneNumber?: string;
      jobTitle?: string;
      department?: string;
      country?: string;
    },
    token: string,
  ) => {
    const response = await this.client.put(`/updateMemberProfile`, payload, {
      headers: { Authorization: token },
    });
    return response.data;
  };
}

// Base URL should point to your enterprise/board backend
export default new TeamController(process.env.REACT_APP_API_URL_ENTERPRISE);
